import {
  WasteGasExportedEmission,
  WasteGasImportedEmission,
  WasteGasParams
} from '@/api/EmissionReportModel'
import {
  Button,
  Card,
  CardBody,
  CardHeader,
  CardTitle,
  DataBlock,
  NumericInputGroup
} from '@/common'
import { useTranslation } from 'react-i18next'
import { UnitLabel } from '@/shared/config/config'
import { useMemo, useState } from 'react'
import calculateWasteGasExportedEmissions from '@/features/reports/formulas/calculateWasteGasExportedEmissions'
import calculateWasteGasImportedEmissions from '@/features/reports/formulas/calculateWasteGasImportedEmissions'
import { useUpdateMethodEmissionReport } from '@/hooks/api/emissionReports'
import { useAppConfig } from '@/shared/hooks/api/appConfig'
import { usePermissions } from '@/shared/hooks/usePermissions'

const WASTE_GAS_EXPORT_DEFAULT_CORRECTION_FACTOR = '0.667'
const NATURAL_GAS_ID = 'natural_gas'

export default function WasteGasReporting({
  close,
  reportId,
  wasteGasImportedEmissions,
  wasteGasExportedEmissions,
  processId
}: {
  close: () => void
  reportId: string
  wasteGasImportedEmissions: WasteGasImportedEmission | null
  wasteGasExportedEmissions: WasteGasExportedEmission | null
  processId: string
}) {
  const { t } = useTranslation()
  const { combustionFuelTypesById } = useAppConfig().config
  const naturalGasEmissionFactor = combustionFuelTypesById[NATURAL_GAS_ID].emissionFactor
  const { checkPermission } = usePermissions()
  const canEditEmissionReport = checkPermission('emissionReport', 'update')
  if (!naturalGasEmissionFactor) {
    throw new Error('Natural gas emission factor not found')
  }

  const { mutateAsync: updateEmissionReport, isPending } = useUpdateMethodEmissionReport(
    reportId,
    processId
  )

  const [wasteGasImported, setWasteGasImported] = useState<WasteGasParams>({
    volume: wasteGasImportedEmissions?.artifacts.volume || '',
    netCalorificValue: wasteGasImportedEmissions?.artifacts.netCalorificValue || '',
    emissionFactor:
      wasteGasImportedEmissions?.artifacts.emissionFactor || naturalGasEmissionFactor.toString()
  })

  const [wasteGasExported, setWasteGasExported] = useState<WasteGasParams>({
    volume: wasteGasExportedEmissions?.artifacts.volume || '',
    netCalorificValue: wasteGasExportedEmissions?.artifacts.netCalorificValue || '',
    emissionFactor:
      wasteGasExportedEmissions?.artifacts.emissionFactor || naturalGasEmissionFactor.toString(),
    correctionFactor:
      wasteGasExportedEmissions?.artifacts.correctionFactor ||
      WASTE_GAS_EXPORT_DEFAULT_CORRECTION_FACTOR
  })

  const wasteGasExportedEmission = useMemo(() => {
    try {
      return calculateWasteGasExportedEmissions(wasteGasExported)
    } catch {
      return 0
    }
  }, [wasteGasExported])

  const wasteGasImportedEmission = useMemo(() => {
    try {
      return calculateWasteGasImportedEmissions(wasteGasImported)
    } catch {
      return 0
    }
  }, [wasteGasImported])

  const saveAndClose = async () => {
    await updateEmissionReport({
      wasteGasImportedEmissionRaw: {
        emission: Number(wasteGasImportedEmission),
        artifacts: wasteGasImported
      },
      wasteGasExportedEmissionRaw: {
        emission: Number(wasteGasExportedEmission),
        artifacts: wasteGasExported
      }
    })

    close()
  }

  const isNextButtonEnabled = Boolean(wasteGasImportedEmission && wasteGasImportedEmission)

  const importedSection = (
    <Card>
      <CardHeader>
        <CardTitle title={t('report:wasteGas.imported')} />
      </CardHeader>
      <CardBody>
        <div className="space-y-2">
          <NumericInputGroup
            label={t('report:wasteGas.fields.volume.label')}
            value={wasteGasImported.volume}
            suffix={UnitLabel.t}
            numericFormat={{
              allowNegative: false
            }}
            onChange={evt => setWasteGasImported({ ...wasteGasImported, volume: evt.target.value })}
          />

          <NumericInputGroup
            label={t('report:wasteGas.fields.netCalorificValue.label')}
            value={wasteGasImported.netCalorificValue}
            suffix={UnitLabel.GJpT}
            numericFormat={{
              decimalScale: 3,
              allowNegative: false
            }}
            onChange={evt =>
              setWasteGasImported({
                ...wasteGasImported,
                netCalorificValue: evt.target.value
              })
            }
          />

          <NumericInputGroup
            label={t('report:wasteGas.fields.emissionFactor.label')}
            value={wasteGasImported.emissionFactor}
            disabled
            suffix={UnitLabel.tCO2ePerTJ}
            onChange={evt =>
              setWasteGasImported({ ...wasteGasImported, emissionFactor: evt.target.value })
            }
          />

          <DataBlock
            label={t('report:wasteGas.fields.emission.label')}
            data={`${wasteGasImportedEmission} ${UnitLabel.tCO2e}`}
          />
        </div>
      </CardBody>
    </Card>
  )

  const exportedSection = (
    <Card>
      <CardHeader>
        <CardTitle title={t('report:wasteGas.exported')} />
      </CardHeader>
      <CardBody>
        <div className="space-y-2">
          <NumericInputGroup
            label={t('report:wasteGas.fields.volume.label')}
            value={wasteGasExported.volume}
            suffix={UnitLabel.t}
            numericFormat={{
              allowNegative: false
            }}
            onChange={evt => setWasteGasExported({ ...wasteGasExported, volume: evt.target.value })}
          />

          <NumericInputGroup
            label={t('report:wasteGas.fields.netCalorificValue.label')}
            value={wasteGasExported.netCalorificValue}
            suffix={UnitLabel.GJpT}
            numericFormat={{
              decimalScale: 3,
              allowNegative: false
            }}
            onChange={evt =>
              setWasteGasExported({
                ...wasteGasExported,
                netCalorificValue: evt.target.value
              })
            }
          />

          <NumericInputGroup
            label={t('report:wasteGas.fields.emissionFactor.label')}
            value={wasteGasExported.emissionFactor}
            suffix={UnitLabel.tCO2ePerTJ}
            disabled
            onChange={evt =>
              setWasteGasExported({ ...wasteGasExported, emissionFactor: evt.target.value })
            }
          />

          <NumericInputGroup
            label={t('report:wasteGas.fields.correctionFactor.label')}
            hint={t('report:wasteGas.fields.correctionFactor.hint')}
            value={wasteGasExported.correctionFactor}
            numericFormat={{
              decimalScale: 3,
              allowNegative: false
            }}
            onChange={evt =>
              setWasteGasExported({ ...wasteGasExported, correctionFactor: evt.target.value })
            }
          />

          <DataBlock
            label={t('report:wasteGas.fields.emission.label')}
            data={`${wasteGasExportedEmission} ${UnitLabel.tCO2e}`}
          />
        </div>
      </CardBody>
    </Card>
  )

  return (
    <div className="space-y-4">
      <div className="grid grid-cols-2 gap-4">
        {importedSection}
        {exportedSection}
      </div>
      <footer className="gap-2 flex justify-end">
        <Button className="min-w-24" variant="neutral" onClick={close}>
          {t('common:actions.cancel')}
        </Button>
        <Button
          className="min-w-24"
          disabled={!isNextButtonEnabled || isPending || !canEditEmissionReport}
          isLoading={isPending}
          onClick={saveAndClose}
        >
          {t('common:actions.save')}
        </Button>
      </footer>
    </div>
  )
}
